// Stylized SVG art for catalog items. Silhouettes are iconic; the colorway does
// the recognition work. Adding a genuinely new shape = one function here.
// All templates draw into a 0 0 200 128 viewBox, toe pointing right.

import type { CatalogItem, ArtId } from "../shared/catalog";

type C = Record<string, string>;
const g = (k: string, c: C, d = "#888") => c[k] ?? d;

const TEMPLATES: Record<ArtId, (c: C) => string> = {
  j1: (c) => `
    <path d="M18,112 Q14,121 27,121 L176,121 Q190,121 188,110 L184,104 L20,104Z" fill="${g("out", c)}"/>
    <path d="M15,86 L189,86 Q193,102 184,107 L23,107 Q13,98 15,86Z" fill="${g("mid", c)}"/>
    <path d="M17,56 L41,54 L47,86 L17,86Z" fill="${g("heel", c)}"/>
    <path d="M15,26 Q15,11 39,11 Q68,11 72,45 Q76,72 52,78 L24,75 Q13,58 15,26Z" fill="${g("collar", c)}"/>
    <path d="M40,9 Q61,5 68,16 L72,45 L47,49 L42,16Z" fill="${g("tongue", c)}"/>
    <path d="M44,32 L114,23 Q124,25 128,69 L60,78 Q46,55 44,32Z" fill="${g("eye", c)}"/>
    <path d="M74,56 L168,56 Q186,56 192,86 L55,88 Q59,64 74,56Z" fill="${g("vamp", c)}"/>
    <path d="M146,50 Q182,52 192,80 L193,88 L156,86 Q142,66 146,50Z" fill="${g("toe", c)}"/>
    <path d="M58,82 Q120,50 160,38 Q173,34 178,50 Q150,60 72,94 Q60,96 58,82Z" fill="${g("swoosh", c)}"/>
    <g fill="${g("lace", c)}"><circle cx="52" cy="30" r="2.4"/><circle cx="66" cy="28" r="2.4"/><circle cx="80" cy="26" r="2.4"/><circle cx="94" cy="24" r="2.4"/><circle cx="108" cy="22" r="2.4"/></g>
    <rect x="22" y="60" width="15" height="6" rx="1.5" fill="${g("wings", c)}"/>`,

  dunk: (c) => `
    <path d="M16,112 Q13,120 25,120 L176,120 Q190,120 188,110 L184,104 L18,104Z" fill="${g("out", c)}"/>
    <path d="M14,84 L190,84 Q194,101 184,106 L20,106 Q12,96 14,84Z" fill="${g("mid", c)}"/>
    <path d="M16,52 L40,50 L46,84 L16,84Z" fill="${g("heel", c)}"/>
    <path d="M16,36 Q16,24 36,24 Q60,24 64,52 L60,74 L22,72 Q13,58 16,36Z" fill="${g("collar", c)}"/>
    <path d="M44,34 L112,26 Q122,28 126,70 L60,76 Q46,54 44,34Z" fill="${g("eye", c)}"/>
    <path d="M72,54 L166,54 Q184,54 190,84 L54,86 Q58,62 72,54Z" fill="${g("vamp", c)}"/>
    <path d="M144,48 Q180,50 190,78 L191,86 L154,84 Q140,64 144,48Z" fill="${g("toe", c)}"/>
    <path d="M56,80 Q118,48 158,36 Q171,32 176,48 Q148,58 70,92 Q58,94 56,80Z" fill="${g("swoosh", c)}"/>
    <g fill="${g("lace", c)}"><circle cx="54" cy="34" r="2.4"/><circle cx="70" cy="31" r="2.4"/><circle cx="86" cy="29" r="2.4"/><circle cx="102" cy="27" r="2.4"/></g>`,

  yz350: (c) => `
    <path d="M18,110 Q13,98 16,90 L188,82 Q195,98 184,106 Q100,120 18,110Z" fill="${g("boost", c)}"/>
    <g stroke="${g("boostL", c)}" stroke-width="1.6" opacity=".5">
      <line x1="30" y1="96" x2="30" y2="110"/><line x1="50" y1="94" x2="50" y2="110"/><line x1="72" y1="92" x2="72" y2="109"/><line x1="96" y1="90" x2="96" y2="108"/><line x1="122" y1="88" x2="122" y2="106"/><line x1="148" y1="86" x2="148" y2="102"/><line x1="170" y1="84" x2="170" y2="98"/></g>
    <path d="M24,90 Q16,48 52,30 Q104,8 156,30 Q188,42 186,74 L182,84 Q100,62 26,88Z" fill="${g("knit", c)}"/>
    <path d="M66,62 L170,34 Q178,36 178,46 L74,78 Q66,72 66,62Z" fill="${g("stripe", c)}"/>
    <path d="M40,32 Q88,16 128,26" stroke="${g("lace", c)}" stroke-width="3" fill="none"/>
    <path d="M24,64 L42,28 L54,72 Z" fill="${g("heelT", c)}"/>`,

  af1: (c) => `
    <path d="M14,112 Q11,121 24,121 L178,121 Q192,121 190,109 L188,100 L16,100Z" fill="${g("out", c)}"/>
    <path d="M12,78 L192,78 Q196,98 188,102 L16,102 Q10,90 12,78Z" fill="${g("mid", c)}"/>
    <circle cx="150" cy="90" r="7" fill="none" stroke="${g("out", c)}" stroke-width="2"/>
    <path d="M16,50 Q16,38 38,38 Q62,38 66,66 L62,78 L20,76 Q13,62 16,50Z" fill="${g("collar", c)}"/>
    <path d="M46,44 L118,36 Q128,38 132,70 L62,78 Q48,58 46,44Z" fill="${g("eye", c)}"/>
    <path d="M74,56 L176,56 Q192,56 196,78 L56,80 Q60,64 74,56Z" fill="${g("vamp", c)}"/>
    <path d="M58,74 Q120,48 160,36 Q173,32 178,48 Q150,58 72,88 Q60,90 58,74Z" fill="${g("swoosh", c)}"/>
    <g fill="${g("lace", c)}"><circle cx="56" cy="42" r="2.4"/><circle cx="74" cy="39" r="2.4"/><circle cx="92" cy="37" r="2.4"/><circle cx="110" cy="35" r="2.4"/></g>`,

  foam: (c) => `
    <path d="M22,100 Q12,64 32,50 Q66,22 122,26 Q182,30 194,68 Q200,98 178,104 Q100,118 40,110Z" fill="${g("body", c)}"/>
    <path d="M18,94 Q100,110 196,82 L196,96 Q100,118 22,106Z" fill="${g("sole", c)}"/>
    <g fill="${g("hole", c)}">
      <ellipse cx="66" cy="48" rx="7" ry="4.6"/><ellipse cx="92" cy="42" rx="7" ry="4.6"/><ellipse cx="118" cy="42" rx="7" ry="4.6"/><ellipse cx="146" cy="48" rx="7" ry="4.6"/>
      <ellipse cx="80" cy="66" rx="5.5" ry="4"/><ellipse cx="112" cy="62" rx="5.5" ry="4"/><ellipse cx="142" cy="66" rx="5.5" ry="4"/></g>`,

  aj4: (c) => `
    <path d="M16,112 Q13,121 26,121 L178,121 Q192,121 190,110 L186,104 L18,104Z" fill="${g("out", c)}"/>
    <path d="M14,82 L190,82 Q194,101 184,106 L20,106 Q12,94 14,82Z" fill="${g("sole", c)}"/>
    <rect x="150" y="86" width="30" height="10" rx="3" fill="none" stroke="${g("out", c)}" stroke-width="2"/>
    <path d="M16,44 Q16,30 40,30 Q120,26 168,44 Q184,50 188,80 L20,82 Q13,62 16,44Z" fill="${g("upper", c)}"/>
    <path d="M60,40 Q66,74 60,80 L52,80 Q44,54 50,40Z M96,34 Q102,72 96,80 L88,80 Q80,50 84,34Z" fill="${g("cage", c)}"/>
    <path d="M40,36 L52,34 L58,44 L44,48Z" fill="${g("wing", c)}"/>
    <g fill="${g("cage", c)}"><circle cx="56" cy="40" r="2.6"/><circle cx="74" cy="37" r="2.6"/><circle cx="92" cy="35" r="2.6"/></g>`,

  hoodie: (c) => `
    <path d="M44,30 L124,30 L150,54 L128,64 L128,116 L40,116 L40,64 L18,54Z" fill="${g("body", c)}"/>
    <path d="M62,28 Q84,8 108,28 Q108,48 84,50 Q60,48 62,28Z" fill="${g("hood", c)}"/>
    <path d="M70,74 Q84,84 98,74 L98,96 L70,96Z" fill="${g("body", c)}" stroke="${g("hood", c)}" stroke-width="1.5"/>
    <rect x="70" y="58" width="28" height="12" rx="2" fill="${g("logo", c)}"/>
    <line x1="76" y1="30" x2="76" y2="42" stroke="${g("hood", c)}" stroke-width="2"/>
    <line x1="92" y1="30" x2="92" y2="42" stroke="${g("hood", c)}" stroke-width="2"/>`,

  shark: (c) => `
    <path d="M46,44 L122,44 L146,66 L126,74 L126,118 L44,118 L44,74 L22,66Z" fill="${g("body", c)}"/>
    <path d="M56,44 Q84,6 112,44 L112,50 L56,50Z" fill="${g("hood", c)}"/>
    <polygon points="58,44 62,52 66,44 70,52 74,44 78,52 82,44 86,52 90,44 94,52 98,44 102,52 106,44 110,52 112,44" fill="${g("body", c)}"/>
    <circle cx="74" cy="30" r="4" fill="#f3f3f3"/><circle cx="94" cy="30" r="4" fill="#f3f3f3"/>
    <circle cx="74" cy="30" r="1.8" fill="#161616"/><circle cx="94" cy="30" r="1.8" fill="#161616"/>
    <rect x="82" y="52" width="4" height="66" fill="${g("hood", c)}"/>`,

  tee: (c) => `
    <path d="M44,32 L66,24 Q84,36 102,24 L124,32 L142,52 L124,62 L124,112 L44,112 L44,62 L26,52Z" fill="${g("body", c)}"/>
    <path d="M66,24 Q84,34 102,24 Q100,32 84,33 Q68,32 66,24Z" fill="${g("body", c)}" stroke="${g("logo", c)}" stroke-width="1" opacity=".6"/>
    <rect x="66" y="46" width="36" height="18" rx="2" fill="${g("logo", c)}"/>`,

  sweats: (c) => `
    <path d="M50,20 L118,20 L120,34 L112,120 L92,120 L84,60 L76,120 L56,120 L48,34Z" fill="${g("body", c)}"/>
    <rect x="48" y="16" width="72" height="10" rx="2" fill="${g("logo", c)}"/>
    <circle cx="66" cy="66" r="9" fill="none" stroke="${g("logo", c)}" stroke-width="2.5"/>`,

  cross: (c) => `
    <path d="M40,24 Q100,40 160,24" stroke="${g("shade", c)}" stroke-width="3" fill="none"/>
    <g fill="${g("metal", c)}" stroke="${g("shade", c)}" stroke-width="1.5">
      <rect x="90" y="40" width="20" height="60" rx="3"/>
      <rect x="70" y="56" width="60" height="18" rx="3"/>
      <circle cx="100" cy="104" r="9"/></g>
    <rect x="94" y="46" width="4" height="46" fill="${g("shade", c)}"/>`,
};

export function artSvg(item: CatalogItem, extra = ""): string {
  const body = TEMPLATES[item.art]?.(item.colors) ?? "";
  return `<svg viewBox="0 0 200 128" preserveAspectRatio="xMidYMid meet" class="art art-${item.art}">${body}${extra}</svg>`;
}

/** just the inner shapes, for when you need to nest in your own <svg> (Shred) */
export function artInner(item: CatalogItem): string {
  return TEMPLATES[item.art]?.(item.colors) ?? "";
}
